import React, { useEffect, useMemo, useRef, useState } from 'react';
import StandardMCQRenderer from './StandardMCQRenderer';

const SYMBOL_SET = ['★', '◆', '▲', '●', '■', '◇', '△', '○', '□', '✦', '⬡', '⬢'];
const GRID_SIZE = 12;
const TIME_LIMIT = 30;

function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (Math.imul(31, hash) + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function generateSymbolGrid(questionId, referenceSymbol) {
  const rng = mulberry32(hashSeed(questionId));
  const grid = Array.from({ length: GRID_SIZE }, (_, index) => ({
    id: index,
    symbol: SYMBOL_SET[Math.floor(rng() * SYMBOL_SET.length)],
    isMatch: false
  }));

  const matchCount = 4 + Math.floor(rng() * 3);
  const positions = Array.from({ length: GRID_SIZE }, (_, index) => index)
    .sort(() => rng() - 0.5)
    .slice(0, matchCount);

  positions.forEach((position) => {
    grid[position].symbol = referenceSymbol;
    grid[position].isMatch = true;
  });

  return { grid, matchCount };
}

function scoreBand(correctHits, falseAlarms, totalTargets) {
  const accuracy = totalTargets > 0 ? (correctHits - falseAlarms) / totalTargets : 0;
  if (accuracy >= 0.9) return 'A';
  if (accuracy >= 0.6) return 'B';
  if (accuracy >= 0.3) return 'C';
  return 'D';
}

export default function ProcessingSpeedRenderer({ question, existingAnswer, onComplete }) {
  if (!question?.id) {
    return (
      <StandardMCQRenderer
        question={question}
        value={existingAnswer?.selectedOption || ''}
        onChange={(selectedOption) => onComplete({ selectedOption })}
        accentColor="var(--cog-primary)"
      />
    );
  }

  const referenceSymbol = SYMBOL_SET[hashSeed(question.id) % SYMBOL_SET.length];
  const { grid, matchCount } = useMemo(
    () => generateSymbolGrid(question.id, referenceSymbol),
    [question.id, referenceSymbol]
  );

  const [selected, setSelected] = useState(() => new Set(existingAnswer?.selectedIndices || []));
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [submitted, setSubmitted] = useState(Boolean(existingAnswer));
  const timerRef = useRef(null);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    if (existingAnswer) {
      setSubmitted(true);
      setSelected(new Set(existingAnswer.selectedIndices || []));
      setTimeLeft(Math.max(0, TIME_LIMIT - Math.round((existingAnswer.timeTakenMs || 0) / 1000)));
      return undefined;
    }

    setSubmitted(false);
    setSelected(new Set());
    setTimeLeft(TIME_LIMIT);
    startedAtRef.current = Date.now();

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [question.id, existingAnswer]);

  useEffect(() => {
    if (timeLeft === 0 && !submitted && !existingAnswer) {
      handleSubmit(true);
    }
  }, [timeLeft, submitted, existingAnswer]);

  const toggle = (id) => {
    if (submitted) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (timedOut = false) => {
    if (submitted) return;
    clearInterval(timerRef.current);
    setSubmitted(true);

    const selectedIndices = Array.from(selected.values());
    const correctHits = selectedIndices.filter((id) => grid[id]?.isMatch).length;
    const falseAlarms = selectedIndices.filter((id) => !grid[id]?.isMatch).length;
    const selectedOption = scoreBand(correctHits, falseAlarms, matchCount);

    onComplete({
      selectedOption,
      selectedIndices,
      correctMatches: correctHits,
      totalMatches: matchCount,
      timeTaken: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
      timeTakenMs: Date.now() - startedAtRef.current,
      rawResponse: {
        type: 'processing_speed',
        correctHits,
        falseAlarms,
        totalTargets: matchCount,
        timedOut
      }
    });
  };

  const selectedIds = useMemo(() => new Set(selected), [selected]);
  const timerPct = (timeLeft / TIME_LIMIT) * 100;

  return (
    <div className="assessment-shell ps-renderer">
      <div className="rounded-2xl border border-white/20 bg-slate-900/55 backdrop-blur-xl p-5">
        <p className="text-sm text-white/75">Reference Symbol</p>
        <div className="mt-2 text-4xl font-bold text-violet-200">{referenceSymbol}</div>
        <p className="mt-2 text-xs text-white/60">Select all matching symbols within 30 seconds.</p>
      </div>

      <div className="mt-3 relative h-2 rounded-full bg-slate-900/55 overflow-hidden">
        <div
          className="ps-timer-bar-fill bg-gradient-to-r from-violet-500 to-purple-600"
          style={{ width: `${timerPct}%` }}
        />
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {grid.map((cell) => {
          const isSelected = selectedIds.has(cell.id);
          const isCorrect = submitted && cell.isMatch;
          const isWrong = submitted && isSelected && !cell.isMatch;
          return (
            <button
              key={`${question.id}_${cell.id}`}
              type="button"
              onClick={() => toggle(cell.id)}
              disabled={submitted}
              className={`h-12 rounded-lg border text-xl transition ${isCorrect ? 'border-emerald-300 bg-emerald-600/35 text-white' : isWrong ? 'border-red-300 bg-red-600/35 text-white' : isSelected ? 'border-violet-300 bg-violet-600/45 text-white' : 'border-white/20 bg-slate-800/60 text-white hover:border-violet-400 hover:bg-slate-700/70'}`}
            >
              {cell.symbol}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className={`text-sm font-semibold ${timeLeft <= 8 ? 'timer-warning text-red-300' : 'text-white/80'}`}>Time Left: {timeLeft}s</p>
        <button
          type="button"
          onClick={() => handleSubmit(false)}
          disabled={submitted}
          className="rounded-lg bg-gradient-to-r from-violet-600 to-purple-700 px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          Submit Grid
        </button>
      </div>
    </div>
  );
}
